/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useSonnerToast } from '@/shared/ui';
import { reportError } from '@/shared/ui/components/basic/error-notification/report-error';
import { CalendarService } from '@services/requests/housien-services/CalendarService';
import type {
  CalendarEventInput,
  CalendarEventUpdateInput,
  EventRecurrenceScope,
} from '@services/requests/housien-models/CalendarEventInput';
import type { BackendRecurrenceScope } from '@services/requests/housien-services/CalendarService.events';
import type {
  CalendarInput,
  CalendarUpdateInput,
} from '@services/requests/housien-models/CalendarInput';
import type { Calendar } from '@services/requests/housien-models/Calendar';
import { agendaKeys } from './agenda-keys';

/**
 * Backend scope values accepted by `_VALID_SCOPES`
 * (`housien-backend-api/housien/services/_calendar_helpers.py`) differ from
 * the UI's `EventRecurrenceScope` ('this'/'following'/'all') — translate at
 * this API boundary so every caller can keep using the UI-facing values.
 */
function toBackendScope(scope: EventRecurrenceScope | undefined): BackendRecurrenceScope | undefined {
  if (scope === 'this') return 'this_only';
  if (scope === 'following') return 'this_and_following';
  return scope;
}

type UpdateEventArgs = {
  eventId: string;
  body: CalendarEventUpdateInput;
  scope?: EventRecurrenceScope;
  occurrence?: string;
};

type DeleteEventArgs = {
  eventId: string;
  scope?: EventRecurrenceScope;
  occurrence?: string;
};

type UpdateCalendarArgs = {
  calendarId: string;
  body: CalendarUpdateInput;
};

/**
 * Event create/update/delete mutations, scoped to one agency.
 * Invalidates the events query and surfaces success/error toasts.
 *
 * @param agencyId The agency id.
 */
export const useEventMutations = (agencyId: string) => {
  const { t } = useTranslation('features/agenda');
  const { showSuccess } = useSonnerToast();
  const queryClient = useQueryClient();

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: agendaKeys.all(agencyId) });

  const createEvent = useMutation({
    mutationFn: (body: CalendarEventInput) =>
      CalendarService.createEvent(agencyId, body),
    onSuccess: () => {
      invalidate();
      showSuccess(t('toast.event_created'));
    },
    onError: (error) => reportError(error, { message: t('toast.event_create_failed') }),
  });

  const updateEvent = useMutation({
    mutationFn: (args: UpdateEventArgs) =>
      CalendarService.updateEvent(
        agencyId,
        args.eventId,
        args.body,
        toBackendScope(args.scope),
        args.occurrence
      ),
    onSuccess: () => {
      invalidate();
      showSuccess(t('toast.event_updated'));
    },
    onError: (error) => reportError(error, { message: t('toast.event_update_failed') }),
  });

  const deleteEvent = useMutation({
    mutationFn: (args: DeleteEventArgs) =>
      CalendarService.deleteEvent(
        agencyId,
        args.eventId,
        toBackendScope(args.scope),
        args.occurrence
      ),
    onSuccess: () => {
      invalidate();
      showSuccess(t('toast.event_deleted'));
    },
    onError: (error) => reportError(error, { message: t('toast.event_delete_failed') }),
  });

  return { createEvent, updateEvent, deleteEvent };
};

/**
 * Calendar create/update/delete mutations, scoped to one agency.
 *
 * @param agencyId The agency id.
 */
export const useCalendarMutations = (agencyId: string) => {
  const { t } = useTranslation('features/agenda');
  const { showSuccess } = useSonnerToast();
  const queryClient = useQueryClient();

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: agendaKeys.calendars(agencyId) });

  const createCalendar = useMutation({
    mutationFn: (body: CalendarInput) =>
      CalendarService.createCalendar(agencyId, body),
    onSuccess: () => {
      invalidate();
      showSuccess(t('toast.calendar_created'));
    },
    onError: (error) => reportError(error, { message: t('toast.calendar_create_failed') }),
  });

  const updateCalendar = useMutation({
    mutationFn: (args: UpdateCalendarArgs) =>
      CalendarService.updateCalendar(agencyId, args.calendarId, args.body),
    onMutate: async (args: UpdateCalendarArgs) => {
      const key = agendaKeys.calendars(agencyId);
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<Calendar[]>(key);
      queryClient.setQueryData<Calendar[]>(key, (current) =>
        (current ?? []).map((calendar) =>
          calendar.calendar_id === args.calendarId
            ? { ...calendar, ...args.body }
            : calendar
        )
      );
      return { previous };
    },
    onError: (error, _args, context) => {
      const previous = (context as { previous?: Calendar[] } | undefined)
        ?.previous;
      if (previous) {
        queryClient.setQueryData(agendaKeys.calendars(agencyId), previous);
      }
      reportError(error, { message: t('toast.calendar_update_failed') });
    },
    onSuccess: () => showSuccess(t('toast.calendar_updated')),
    onSettled: () => invalidate(),
  });

  const deleteCalendar = useMutation({
    mutationFn: (calendarId: string) =>
      CalendarService.deleteCalendar(agencyId, calendarId),
    onSuccess: () => {
      invalidate();
      showSuccess(t('toast.calendar_deleted'));
    },
    onError: (error) => reportError(error, { message: t('toast.calendar_delete_failed') }),
  });

  return { createCalendar, updateCalendar, deleteCalendar };
};
